import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import subscriptionService, { SubscriptionStatus } from '../services/subscriptionService';
import i18n from '../i18n';

interface SubscriptionContextType {
  subscription: SubscriptionStatus | null;
  status: 'active' | 'suspended' | 'canceled' | 'trial' | 'error' | 'loading' | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

interface SubscriptionProviderProps {
  children: ReactNode;
}

export const SubscriptionProvider = ({ children }: SubscriptionProviderProps) => {
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [status, setStatus] = useState<'loading' | 'active' | 'suspended' | 'canceled' | 'trial' | 'error'>('loading');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSubscriptionStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await subscriptionService.getSubscriptionStatus();
      setSubscription(data);
      //setStatus(data.subscriptionStatus);
      setStatus('active');
    } catch (err: any) {
      console.error("Failed to fetch subscription status:", err);
      setError(err.message || "Failed to load subscription status");
      setStatus('error');
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubscriptionStatus();
    // Listen for a custom event to refresh subscription status after login
    window.addEventListener('userLoggedIn', fetchSubscriptionStatus);
    return () => {
      window.removeEventListener('userLoggedIn', fetchSubscriptionStatus);
    };
  }, [fetchSubscriptionStatus]);

  const refresh = async () => {
    await fetchSubscriptionStatus();
  };

  return (
    <SubscriptionContext.Provider value={{ subscription, status, loading, error, refresh }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = (): SubscriptionContextType => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error("useSubscription must be used within a SubscriptionProvider");
  }
  return context;
};

